import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';

const PARAM = 'replay';
const MINUTE_FORMAT = "yyyy-LL-dd'T'HH:mm";
const PARAM_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

export interface IReplayState {
  open: boolean;
  date: string | null;
  atMs: number | null;
  openBar(): void;
  close(): void;
  selectDate(date: string): void;
  commitAt(ms: number): void;
}

/**
 * Replay URL state: `?replay=YYYY-MM-DDTHH:mm` in the site's local wall time.
 * Converted with luxon (already a frontend dependency) so the param means the
 * same instant for every viewer regardless of their own timezone. The URL is
 * written only on commit/select/close (replace, other params untouched) and
 * never per playback tick. `open` is true while the param is present or after
 * `openBar()`.
 */
export function useReplayState(tz: string): IReplayState {
  const [searchParams, setSearchParams] = useSearchParams();
  const [opened, setOpened] = useState(false);
  const raw = searchParams.get(PARAM);

  const parsed = useMemo(() => {
    if (!raw || !PARAM_RE.test(raw)) return { date: null, atMs: null };
    const at = DateTime.fromISO(raw, { zone: tz });
    return at.isValid ? { date: raw.slice(0, 10), atMs: at.toMillis() } : { date: null, atMs: null };
  }, [raw, tz]);

  const write = useCallback(
    (value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(PARAM, value);
          else next.delete(PARAM);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openBar = useCallback(() => setOpened(true), []);
  const close = useCallback(() => {
    setOpened(false);
    write(null);
  }, [write]);
  // A newly picked day starts at its site-local midnight; the bar re-commits
  // at the day's first in once its frames arrive.
  const selectDate = useCallback((date: string) => write(`${date}T00:00`), [write]);
  const commitAt = useCallback(
    (ms: number) => write(DateTime.fromMillis(ms, { zone: tz }).toFormat(MINUTE_FORMAT)),
    [write, tz],
  );

  const open = opened || !!raw;
  return useMemo(
    () => ({ open, ...parsed, openBar, close, selectDate, commitAt }),
    [open, parsed, openBar, close, selectDate, commitAt],
  );
}
